import { useEditProviderUserInformation } from '@/queries/provider-permission-management/mutations'
import {
  ProviderUser,
  ProviderUserRole,
  ProviderUserStatusCode,
} from '@/queries/provider-permission-management/schema'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shad/ui/dialog'
import ManageUserForm, { UserFormValues } from './manage-user-form'

interface EditUserDialogProps {
  onOpenChange: (open: boolean) => void
  selectedContact: ProviderUser
  providerId: string
  roles: ProviderUserRole[]
}

export default function EditUserDialog({
  onOpenChange,
  selectedContact,
  roles,
  providerId,
}: EditUserDialogProps) {
  const editContactInformationMutation = useEditProviderUserInformation({
    providerId: providerId,
  })

  const defaultValues = {
    firstName: selectedContact.first_name ?? '',
    lastName: selectedContact.last_name ?? '',
    email: selectedContact.email,
    role: selectedContact.role.id.toString(),
    status: selectedContact.status,
  }

  const handleSubmit = (data: UserFormValues) => {
    const statusToSend =
      data.status === ProviderUserStatusCode.ACTIVE ||
      data.status === ProviderUserStatusCode.INACTIVE
        ? data.status
        : undefined
    editContactInformationMutation.mutate(
      {
        email: data.email,
        userId: selectedContact.id,
        status: statusToSend,
        firstName: data.firstName,
        lastName: data.lastName,
        roleId: data.role,
      },
      {
        onSuccess: () => {
          onOpenChange(false)
        },
      }
    )
  }

  const handleOpenChange = (newOpen: boolean) => {
    // Prevent closing the dialog when mutation is pending
    if (!newOpen && editContactInformationMutation.status === 'pending') {
      return
    }
    onOpenChange(newOpen)
  }

  return (
    <Dialog open={true} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-lg"
        onInteractOutside={(e) => {
          if (editContactInformationMutation.status === 'pending') {
            e.preventDefault()
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>Edit User</DialogTitle>
        </DialogHeader>

        <ManageUserForm
          mutationStatus={editContactInformationMutation.status}
          roles={roles}
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
          submitButtonText="Save Changes"
          showStatusToggle={true}
        />
      </DialogContent>
    </Dialog>
  )
}
